/**
 * Launchpad accessibility enhancements
 * --------------------------------------
 * Runtime fixes for WCAG 2.1 AA issues that are easier (and safer)
 * to patch globally than by touching every template and app-injected DOM node.
 *
 * Covers audit findings:
 *   F-02  aria-hidden containers holding focusable descendants
 *   F-03  Remove the visually-hidden compliance H1 when a real H1 is already present
 *   F-05  Add a minimal pause/play control to any autoplay <video>
 *   F-07  Warn screen-reader users when a link opens in a new tab/window
 *   F-08  Add descriptive aria-labels to generic link text ("Shop now", "Learn more", etc.)
 *   F-01  Backstop: add a fallback title to any <iframe> still missing one (app-injected frames)
 */
(function () {
  'use strict';

  var GENERIC_LINK_PATTERNS = /^(click here|read more|learn more|here|more|shop now|buy now|view more|details|explore)$/i;

  /* -----------------------------------------------------------
   * F-03: prevent duplicate H1
   * If a real H1 already exists, remove our visually-hidden fallback.
   * If not, keep it so the page still has an H1.
   * --------------------------------------------------------- */
  function dedupeHiddenPageTitle() {
    var hidden = document.querySelector('main h1[data-a11y-page-title]');
    if (!hidden) return;
    var realH1 = document.querySelector('main h1:not([data-a11y-page-title])');
    if (realH1) hidden.remove();
  }

  /* -----------------------------------------------------------
   * F-02: aria-hidden containers holding focusable descendants
   * Apply `inert` (or tabindex="-1" fallback) to focusable descendants
   * so keyboard users can't tab into hidden content.
   * --------------------------------------------------------- */
  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  function sealAriaHidden(root) {
    var hiddenHosts = (root || document).querySelectorAll('[aria-hidden="true"]');
    hiddenHosts.forEach(function (host) {
      if (host.hasAttribute('inert')) return;
      // Prefer native `inert` when supported; otherwise neutralize tab stops.
      if ('inert' in HTMLElement.prototype) {
        host.inert = true;
      } else {
        host.querySelectorAll(FOCUSABLE).forEach(function (el) {
          if (!el.hasAttribute('data-a11y-prev-tabindex')) {
            el.setAttribute('data-a11y-prev-tabindex', el.getAttribute('tabindex') || '');
          }
          el.setAttribute('tabindex', '-1');
        });
      }
    });
  }

  // Observe future aria-hidden changes (cart drawer open/close, etc.)
  function observeAriaHiddenChanges() {
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type !== 'attributes' || m.attributeName !== 'aria-hidden') return;
        var el = m.target;
        if (el.getAttribute('aria-hidden') === 'true') {
          sealAriaHidden(el.parentNode || document);
        } else {
          // Restore tabindex on reveal (fallback path only).
          el.querySelectorAll('[data-a11y-prev-tabindex]').forEach(function (node) {
            var prev = node.getAttribute('data-a11y-prev-tabindex');
            if (prev === '') node.removeAttribute('tabindex');
            else node.setAttribute('tabindex', prev);
            node.removeAttribute('data-a11y-prev-tabindex');
          });
          if ('inert' in HTMLElement.prototype) el.inert = false;
        }
      });
    });
    mo.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  /* -----------------------------------------------------------
   * F-07: new-window link warnings
   * Any <a target="_blank"> without "new tab" / "new window" in its
   * accessible name gets an aria-label appended, plus rel hardening.
   * --------------------------------------------------------- */
  function annotateNewWindowLinks(root) {
    var links = (root || document).querySelectorAll('a[target="_blank"]');
    links.forEach(function (a) {
      // Security: always add noopener on _blank.
      var rel = (a.getAttribute('rel') || '').toLowerCase();
      if (rel.indexOf('noopener') === -1) {
        a.setAttribute('rel', (rel + ' noopener noreferrer').trim());
      }

      var textName = (a.textContent || '').trim();
      var labelled = a.getAttribute('aria-label') || '';
      var combined = (textName + ' ' + labelled).toLowerCase();
      if (combined.indexOf('new tab') !== -1 || combined.indexOf('new window') !== -1) return;

      var base = labelled || textName || a.getAttribute('title') || 'Link';
      a.setAttribute('aria-label', base + ' (opens in new tab)');
    });
  }

  /* -----------------------------------------------------------
   * F-08: generic link text
   * If a link's visible text is something like "Shop now", try to
   * derive a meaningful aria-label from its surroundings.
   * --------------------------------------------------------- */
  function clarifyGenericLinks(root) {
    var links = (root || document).querySelectorAll('a');
    links.forEach(function (a) {
      if (a.getAttribute('aria-label')) return;
      var text = (a.textContent || '').trim();
      if (!text || !GENERIC_LINK_PATTERNS.test(text)) return;

      // Look for a preceding heading or the nearest section/container heading.
      var context = '';
      var scope = a.closest('section, article, li, .card, .card__content, .product-card-wrapper');
      if (scope) {
        var h = scope.querySelector('h1, h2, h3, h4, .card__heading, .product-card__title');
        if (h) context = h.textContent.trim();
      }
      if (!context) {
        // Fall back to the product title on a PDP-ish page.
        var pageH1 = document.querySelector('main h1');
        if (pageH1) context = pageH1.textContent.trim();
      }
      if (context) {
        a.setAttribute('aria-label', text + ' — ' + context);
      }
    });
  }

  /* -----------------------------------------------------------
   * F-01: iframe title backstop
   * Catches iframes rendered by Shopify apps or late-loaded scripts.
   * --------------------------------------------------------- */
  function backfillIframeTitles(root) {
    var frames = (root || document).querySelectorAll('iframe:not([title]), iframe[title=""]');
    frames.forEach(function (f) {
      var guess =
        f.getAttribute('aria-label') ||
        f.getAttribute('name') ||
        (f.src ? inferTitleFromSrc(f.src) : 'Embedded content');
      f.setAttribute('title', guess);
    });
  }

  function inferTitleFromSrc(src) {
    try {
      var host = new URL(src).hostname;
      if (/youtube|youtu\.be/.test(host)) return 'Video player';
      if (/vimeo/.test(host)) return 'Video player';
      if (/shopify/.test(host)) return 'Shopify content frame';
      if (/recaptcha|google/.test(host)) return 'Security verification';
      return 'Embedded content';
    } catch (e) {
      return 'Embedded content';
    }
  }

  /* -----------------------------------------------------------
   * F-05: pause/play control for autoplaying <video>
   * Adds a small corner button (brand-consistent, visually small).
   * Does NOT touch iframe-based videos (those use their provider's controls).
   * --------------------------------------------------------- */
  function addVideoPauseControls(root) {
    var videos = (root || document).querySelectorAll('video[autoplay]:not([controls]):not([data-a11y-controls])');
    videos.forEach(function (video) {
      video.setAttribute('data-a11y-controls', '');
      if (!video.getAttribute('aria-label')) {
        video.setAttribute('aria-label', 'Background video');
      }

      var parent = video.parentElement;
      if (!parent) return;
      var computed = getComputedStyle(parent);
      if (computed.position === 'static') parent.style.position = 'relative';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-video-toggle';
      btn.setAttribute('aria-label', 'Pause background video');
      btn.innerHTML = '<span class="a11y-video-toggle__icon" aria-hidden="true">❚❚</span>';

      btn.addEventListener('click', function () {
        if (video.paused) {
          video.play();
          btn.setAttribute('aria-label', 'Pause background video');
          btn.querySelector('.a11y-video-toggle__icon').textContent = '❚❚';
        } else {
          video.pause();
          btn.setAttribute('aria-label', 'Play background video');
          btn.querySelector('.a11y-video-toggle__icon').textContent = '►';
        }
      });

      parent.appendChild(btn);
    });
  }

  /* -----------------------------------------------------------
   * Init
   * --------------------------------------------------------- */
  function init() {
    dedupeHiddenPageTitle();
    sealAriaHidden();
    observeAriaHiddenChanges();
    annotateNewWindowLinks();
    clarifyGenericLinks();
    backfillIframeTitles();
    addVideoPauseControls();

    // Re-run enrichments on DOM additions (product variants, cart updates, app-injected frames, etc.)
    var debounce;
    var mo = new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        annotateNewWindowLinks();
        clarifyGenericLinks();
        backfillIframeTitles();
        addVideoPauseControls();
        sealAriaHidden();
      }, 250);
    });
    mo.observe(document.body, { subtree: true, childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
